// libs
import classNames from 'classnames'
import React, {
  KeyboardEvent,
  Ref,
  SyntheticEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'

// classes
import Icon, { IconSize } from 'src/components/forms/icon/icon'
import TextInput, {
  TextInputHandle
} from 'src/components/text_input/text_input'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import { IKeyValue } from 'src/interfaces/'

// helpers
import { textResources } from 'src/lang/de'
import {
  COLOR_TYPE_GRAY_TINT_4,
  COLOR_TYPE_GRAY_TINT_9
} from 'src/utils/color'
import './input_field.scss'

export interface IProps {
  spellCheck?: boolean;
  autoCorrect?: 'on' | 'off';
  autoComplete?:
    | 'off'
    | 'current-password'
    | 'username'
    | 'new-password'
    | 'one-time-code'
    | 'url';
  autoFocus?: boolean;
  assistiveText?: string;
  className?: string;
  classNameInput?: string;
  defaultValue?: string;
  disabled?: boolean;
  errorText?: string;
  form?: string;
  floatLabel?: boolean;
  label?: string;
  required?: boolean;
  showEmoji?: boolean;
  iconSize?: IconSize;
  iconLeft?: string;
  iconRight?: string;
  min?: string;
  max?: string;
  maxLength?: number;
  maxHeight?: number;
  name: string;
  onBlur?: (event: SyntheticEvent<HTMLElement>) => void;
  onFocus?: (event: SyntheticEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onInput?: (event: SyntheticEvent<HTMLElement>) => void;
  onChange?: (value: string, name: string) => void;
  onRightIconClick?: (event: React.SyntheticEvent<HTMLElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  initialRows?: number; // textarea when rows is given
  type?: string; // HTML native input type e.g. 'number', 'text', 'email', 'url', 'tel'
  value?: string;
  valid?: boolean;
  noBackground?: boolean;
}

export interface InputFieldHandle {
  blurInput: () => void;
  focusInput: () => void;
}

const clsBlock: string = CLASS_PREFIX + 'input-field'
const clsBlockFloatLabel: string = `${clsBlock}__float-label`
const clsBlockStaticLabel: string = `${clsBlock}__static-label`
const clsBlockBody: string = `${clsBlock}__body`
const clsBlockInnerBody: string = `${clsBlock}__inner-body`
const clsBlockInput: string = `${clsBlock}__input`
const clsBlockIcon: string = `${clsBlock}__icon`
const clsTextarea: string = `${clsBlock}__textarea`

const InputField = forwardRef<InputFieldHandle, IProps>(
  (props, ref: Ref<InputFieldHandle>) => {
    const {
      assistiveText,
      errorText,
      className,
      classNameInput,
      disabled,
      floatLabel = true,
      label,
      name,
      onRightIconClick,
      placeholder,
      iconSize,
      iconLeft,
      iconRight,
      required,
      initialRows,
      valid,
      showEmoji,
      noBackground,
      value,
      defaultValue,
      onFocus,
      onBlur,
      onChange,
      onKeyDown,
      spellCheck,
      autoCorrect,
      autoComplete,
      autoFocus,
      form,
      min,
      max,
      maxLength,
      readOnly,
      type = 'text'
    } = props

    const inputRef = useRef<HTMLInputElement>(null)
    const textInputRef = useRef<TextInputHandle>(null)
    const [focus, setFocus] = useState(false)
    const [isTouched, setIsTouched] = useState(false)
    const [innerValue, setInnerValue] = useState(value || defaultValue || '')

    useEffect(() => {
      if (value !== undefined) {
        setInnerValue(value)
      }
    }, [value])

    const focusInput = useCallback(() => {
      if (initialRows) {
        textInputRef.current?.focus()
      } else {
        inputRef.current?.focus()
      }
    }, [initialRows])

    const blurInput = useCallback(() => {
      if (initialRows) {
        textInputRef.current?.blur()
      } else {
        inputRef.current?.blur()
      }
    }, [initialRows])

    useImperativeHandle(
      ref,
      () => ({
        blurInput,
        focusInput
      }),
      [blurInput, focusInput]
    )

    const handleFocus = useCallback(
      (event: SyntheticEvent<HTMLElement>) => {
        setFocus(true)
        if (onFocus) {
          onFocus(event)
        }
      },
      [onFocus]
    )

    const handleBlur = useCallback(
      (event: SyntheticEvent<HTMLElement>) => {
        setFocus(false)
        if (onBlur) {
          onBlur(event)
        }
      },
      [onBlur]
    )

    const onInputChange = useCallback(
      (event: SyntheticEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement
        setInnerValue(target.value)
        setIsTouched(true)
        if (onChange) {
          onChange(target.value, target.name)
        }
      },
      [onChange]
    )

    const onTextAreaChange = useCallback(
      (updatedValue: string) => {
        setInnerValue(updatedValue)
        setIsTouched(true)
        if (onChange) {
          onChange(updatedValue, name)
        }
      },
      [name, onChange]
    )

    const inputCls: IKeyValue = useMemo(() => {
      const cls: IKeyValue = {}
      cls[`${clsBlockInput}`] = true
      if (classNameInput) {
        cls[classNameInput] = true
      }
      if (iconLeft) {
        cls[`${clsBlockInput}--icon-left`] = true
      }
      if (iconRight) {
        cls[`${clsBlockInput}--icon-right`] = true
      }
      return cls
    }, [classNameInput, iconLeft, iconRight])

    const cls: IKeyValue = {}
    const isEmpty: boolean = !innerValue.trim()
    const isValid = valid === undefined ? true : valid
    const invalidState = !isValid || (required && isEmpty && isTouched)

    const errorMessage =
      required && isEmpty ? textResources.shared.required : errorText
    const showErrorMessage = errorMessage && invalidState
    const showAssistiveMessage =
      (assistiveText && invalidState && !showErrorMessage) ||
      (assistiveText && !invalidState)

    cls[`${clsBlock}`] = true
    cls[`${clsBlock}--invalid`] = invalidState
    cls[`${clsBlock}--touched`] = isTouched
    cls[`${clsBlock}--empty`] = isEmpty
    cls[`${className}`] = className

    const clsLabel: IKeyValue = floatLabel
      ? {
        [`${clsBlockFloatLabel}`]: true,
        [`${clsBlockFloatLabel}--disabled`]: disabled,
        [`${clsBlockFloatLabel}--top`]: focus || !isEmpty || placeholder
      }
      : {
        [`${clsBlockStaticLabel}`]: true
      }
    const clsBody: IKeyValue = {
      [`${clsBlockBody}`]: true,
      [`${clsBlockBody}--not-empty`]: !isEmpty,
      [`${clsBlockBody}--invalid`]: invalidState,
      [`${clsBlockBody}--disabled`]: disabled,
      [`${clsBlockBody}--no-bg`]: noBackground,
      [`${clsBlockBody}--float-label`]: floatLabel
    }

    const clsInnerBody: IKeyValue = {
      [`${clsBlockInnerBody}`]: true,
      [`${clsBlockInnerBody}--float-label`]: floatLabel
    }

    const clsBlockName = clsBlock + name

    return (
      <div className={classNames(cls)}>
        {!floatLabel && (
          <label className={classNames(clsLabel)} htmlFor={clsBlockName}>
            {label}
          </label>
        )}
        <div className={classNames(clsBody)} onClick={focusInput}>
          {iconLeft && (
            <div className={`${clsBlockIcon}-left`}>
              <Icon
                color={
                  disabled ? COLOR_TYPE_GRAY_TINT_4 : COLOR_TYPE_GRAY_TINT_9
                }
                name={iconLeft}
                size={24}
              />
            </div>
          )}
          <div className={classNames(clsInnerBody)}>
            {initialRows
              ? (
                <TextInput
                  {...props}
                  onChange={onTextAreaChange}
                  onBlur={handleBlur}
                  id={clsBlockName}
                  className={
                    classNameInput
                      ? `${clsTextarea} ${classNameInput}`
                      : clsTextarea
                  }
                  required={true}
                  onFocus={handleFocus}
                  ref={textInputRef}
                  invalidState={!!invalidState}
                />
              )
              : (
                <input
                  type={type}
                  autoComplete={autoComplete}
                  spellCheck={spellCheck}
                  autoCorrect={autoCorrect}
                  autoFocus={autoFocus}
                  className={classNames(inputCls)}
                  defaultValue={defaultValue}
                  disabled={disabled}
                  form={form}
                  id={clsBlock + name}
                  placeholder={placeholder}
                  min={min}
                  max={max}
                  maxLength={maxLength}
                  name={name}
                  required={required}
                  value={value}
                  onBlur={handleBlur}
                  onChange={onInputChange}
                  onFocus={handleFocus}
                  ref={inputRef}
                  readOnly={readOnly}
                  onKeyDown={onKeyDown}
                />
              )}
            {floatLabel && (
              <label className={classNames(clsLabel)} htmlFor={clsBlockName}>
                {label}
              </label>
            )}
          </div>
          {!invalidState && !showEmoji && iconRight && (
            <div className={`${clsBlockIcon}-right`} onClick={onRightIconClick}>
              <Icon
                color={
                  disabled ? COLOR_TYPE_GRAY_TINT_4 : COLOR_TYPE_GRAY_TINT_9
                }
                name={iconRight}
                size={iconSize || 24}
              />
            </div>
          )}
        </div>
        {showErrorMessage && (
          <div className={`${clsBlock}__error-text`}>{errorMessage}</div>
        )}
        {showAssistiveMessage && (
          <div className={`${clsBlock}__assistive-text`}>{assistiveText}</div>
        )}
      </div>
    )
  }
)

InputField.displayName = 'InputField'

export default memo(InputField)
